import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { patientApi, patientErr } from "@/lib/patientApi";
import { Play, Pause, SkipBack, SkipForward, X, MusicNotes, SpinnerGap } from "@phosphor-icons/react";

const ORIGIN = API.replace(/\/api$/, "");
const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// Full-audio player using a short-lived signed URL. Persists & resumes position.
export const AudioPlayer = ({ item, onClose }) => {
  const audioRef = useRef(null);
  const startPos = useRef(item.position_sec || 0);
  const lastSaved = useRef(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(item.position_sec || 0);
  const [dur, setDur] = useState(item.duration_sec || 0);
  const [err, setErr] = useState("");

  const savePos = useCallback((sec) => {
    const s = Math.max(0, Math.floor(sec || 0));
    if (Math.abs(s - lastSaved.current) < 3) return;
    lastSaved.current = s;
    patientApi.put(`/patient/playback/${item.product_id}`, { position_sec: s }).catch(() => {});
  }, [item.product_id]);

  useEffect(() => {
    let cancelled = false;
    patientApi.post(`/patient/audio/${item.product_id}/stream-url`)
      .then((r) => {
        if (cancelled) return;
        const a = audioRef.current;
        if (!a) return;
        a.src = ORIGIN + r.data.url;
        a.load();
      })
      .catch((e) => { if (!cancelled) { setErr(patientErr(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [item.product_id]);

  // MediaSession (lock screen / notification controls)
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: item.title, artist: item.category || "Relaksasi HalloGBS", album: "HalloGBS",
    });
    const a = () => audioRef.current;
    navigator.mediaSession.setActionHandler("play", () => a()?.play());
    navigator.mediaSession.setActionHandler("pause", () => a()?.pause());
    navigator.mediaSession.setActionHandler("seekbackward", () => { if (a()) a().currentTime = Math.max(0, a().currentTime - 15); });
    navigator.mediaSession.setActionHandler("seekforward", () => { if (a()) a().currentTime = Math.min(a().duration || 0, a().currentTime + 15); });
    return () => {
      ["play", "pause", "seekbackward", "seekforward"].forEach((k) => {
        try { navigator.mediaSession.setActionHandler(k, null); } catch { /* noop */ }
      });
    };
  }, [item.title, item.category]);

  // Save position on unmount
  useEffect(() => () => { if (audioRef.current) savePos(audioRef.current.currentTime); }, [savePos]);

  const onMeta = () => {
    const a = audioRef.current;
    setDur(a.duration || item.duration_sec || 0);
    if (startPos.current && startPos.current < (a.duration || Infinity) - 2) a.currentTime = startPos.current;
    setLoading(false);
  };
  const onTime = () => { const a = audioRef.current; setCur(a.currentTime); savePos(a.currentTime); };
  const toggle = () => { const a = audioRef.current; if (!a) return; a.paused ? a.play().catch(() => {}) : a.pause(); };
  const seek = (delta) => { const a = audioRef.current; if (a) a.currentTime = Math.min(dur || 0, Math.max(0, a.currentTime + delta)); };
  const scrub = (e) => { const a = audioRef.current; if (a) { a.currentTime = Number(e.target.value); setCur(a.currentTime); } };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="audio-player">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl">
        <div className="mb-4 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#DB2777]"><MusicNotes size={14} weight="fill" /> {item.category || "Relaksasi"}</span>
          <button data-testid="ap-close" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="mx-auto mb-5 flex h-32 w-32 items-center justify-center rounded-3xl bg-gradient-to-br from-[#EEE6FF] to-[#FDE7F1]">
          <MusicNotes size={52} weight="duotone" className="text-[#7C4DE0]" />
        </div>
        <h2 data-testid="ap-title" className="text-center font-heading text-lg font-extrabold text-[#1E1B4B]">{item.title}</h2>

        <audio ref={audioRef} preload="metadata" onLoadedMetadata={onMeta} onTimeUpdate={onTime}
          onPlay={() => setPlaying(true)} onPause={() => { setPlaying(false); savePos(audioRef.current?.currentTime); }}
          onEnded={() => { setPlaying(false); savePos(0); lastSaved.current = -1; }}
          onError={() => { setErr("Gagal memuat audio. Coba buka ulang."); setLoading(false); }} />

        {err ? (
          <p data-testid="ap-error" className="mt-4 rounded-xl bg-rose-50 px-3 py-2 text-center text-sm text-rose-600">{err}</p>
        ) : (
          <>
            <div className="mt-5">
              <input data-testid="ap-scrub" type="range" min={0} max={dur || 0} step={1} value={cur} onChange={scrub}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-pink-100 accent-[#EC4899]" disabled={loading} />
              <div className="mt-1 flex justify-between text-xs font-semibold text-slate-500">
                <span data-testid="ap-cur">{fmt(cur)}</span><span>{fmt(dur)}</span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-6">
              <button data-testid="ap-back" onClick={() => seek(-15)} className="text-slate-500 hover:text-[#7C4DE0]"><SkipBack size={28} weight="fill" /></button>
              <button data-testid="ap-toggle" onClick={toggle} disabled={loading}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-[#7C4DE0] text-white shadow-lg shadow-purple-300 transition-transform active:scale-95 disabled:opacity-60">
                {loading ? <SpinnerGap size={28} weight="bold" className="animate-spin" /> : playing ? <Pause size={30} weight="fill" /> : <Play size={30} weight="fill" className="ml-0.5" />}
              </button>
              <button data-testid="ap-fwd" onClick={() => seek(15)} className="text-slate-500 hover:text-[#7C4DE0]"><SkipForward size={28} weight="fill" /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
